import { logger } from '@/core/logger';
import { WELCOME_EMIRATES_ID_LOGIN_PATH } from '@/modules/bloom-universal/constants/routes';
import { WELCOME_PATH } from '@/modules/welcome/utils/welcomeLoginQuery';

/** External entry marker, e.g. `?redirectionFrom=masterplan`. */
const DEEP_LINK_SOURCE_PARAM = 'redirectionFrom';
/** Same query key `navigateToWelcomeAfterLogout` uses. */
const WELCOME_REDIRECT_PARAM = 'redirect';
const ALLOWED_PATH_PREFIX = '/properties-web/';

/** Survives only the UAE Pass round trip (fixed callback URL drops our query). */
const UAE_PASS_STASH_KEY = 'adha_uae_pass_deep_link';
/**
 * Welcome's "already authenticated" redirect can fire after the login flow has
 * navigated away (URL no longer has `redirect`); it must reuse the same target.
 */
const CONSUMED_GRACE_MS = 5000;

/** Restored from the UAE Pass stash for this Welcome visit. */
let restoredUaePassDeepLink: string | null = null;
let consumedDeepLink: Readonly<{ path: string; at: number }> | null = null;

/** Same-origin catalogue paths only (no `//host` open redirects). */
function isAllowedDeepLinkPath(path: string | null | undefined): path is string {
  return Boolean(path?.startsWith(ALLOWED_PATH_PREFIX) && !path.startsWith('//'));
}

/** React Native defines `window` without `location` / `sessionStorage`. */
function getBrowserLocation(): Location | null {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }
  return window.location;
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Current URL when it is a catalogue link opened from an external source. */
export function readExternalAdhaDeepLink(): string | null {
  const location = getBrowserLocation();
  if (!location) {
    return null;
  }
  const source = new URLSearchParams(location.search).get(DEEP_LINK_SOURCE_PARAM)?.trim();
  if (!source) {
    return null;
  }
  const path = `${location.pathname}${location.search}`;
  return isAllowedDeepLinkPath(path) ? path : null;
}

/** Welcome + Emirates ID login, carrying the unit link as `?redirect=`. */
export function buildWelcomeDeepLinkLoginHref(path: string): string {
  const separator = WELCOME_EMIRATES_ID_LOGIN_PATH.includes('?') ? '&' : '?';
  return `${WELCOME_EMIRATES_ID_LOGIN_PATH}${separator}${WELCOME_REDIRECT_PARAM}=${encodeURIComponent(path)}`;
}

function readWelcomeUrlDeepLink(): string | null {
  const location = getBrowserLocation();
  if (!location) {
    return null;
  }
  const pathname = location.pathname.replace(/\/$/, '') || WELCOME_PATH;
  if (pathname !== WELCOME_PATH) {
    return null;
  }
  const path = new URLSearchParams(location.search).get(WELCOME_REDIRECT_PARAM);
  return isAllowedDeepLinkPath(path) ? path : null;
}

/** Before leaving for UAE Pass: keep the Welcome `redirect` for the return trip. */
export function stashAdhaDeepLinkForUaePass(): void {
  const storage = getSessionStorage();
  const path = readWelcomeUrlDeepLink();
  if (!storage || !path) {
    return;
  }
  try {
    storage.setItem(UAE_PASS_STASH_KEY, path);
  } catch (error) {
    logger.warn('adhaDeepLinkRedirect', 'Failed to stash deep link for UAE Pass', { error });
  }
}

/**
 * Welcome mount: on a UAE Pass return take the stashed link back; on any other
 * visit drop a leftover stash (UAE Pass abandoned).
 */
export function restoreAdhaDeepLinkAfterUaePass(isUaePassReturn: boolean): void {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  try {
    const stashed = storage.getItem(UAE_PASS_STASH_KEY);
    storage.removeItem(UAE_PASS_STASH_KEY);
    if (isUaePassReturn && isAllowedDeepLinkPath(stashed)) {
      restoredUaePassDeepLink = stashed;
    }
  } catch {
    // Storage unavailable: nothing to restore.
  }
}

/** Login abandoned / non-ADHA session: forget any carried unit link. */
export function clearAdhaDeepLink(): void {
  restoredUaePassDeepLink = null;
  consumedDeepLink = null;
  const storage = getSessionStorage();
  try {
    storage?.removeItem(UAE_PASS_STASH_KEY);
  } catch {
    // Storage unavailable: nothing to clear.
  }
}

/** Login flow: take the target once (URL `redirect`, else UAE Pass restore). */
export function consumeAdhaPostLoginDeepLink(): string | null {
  const path = readWelcomeUrlDeepLink() ?? restoredUaePassDeepLink;
  restoredUaePassDeepLink = null;
  consumedDeepLink = path ? { path, at: Date.now() } : null;
  return path;
}

/** Target for Welcome's authenticated redirect (does not consume). */
export function resolveAdhaPostLoginDeepLink(): string | null {
  const path = readWelcomeUrlDeepLink() ?? restoredUaePassDeepLink;
  if (path) {
    return path;
  }
  if (consumedDeepLink && Date.now() - consumedDeepLink.at <= CONSUMED_GRACE_MS) {
    return consumedDeepLink.path;
  }
  consumedDeepLink = null;
  return null;
}
